import axios from 'axios';
import { API_URL } from '../config';

// Create an axios instance with enhanced error handling
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Set reasonable timeouts to prevent hanging requests
  timeout: 10000,
});

// Add request interceptor for handling requests
api.interceptors.request.use(
  (config) => {
    // You could add request logging or auth tokens here
    // Ensure endpoints don't have trailing slashes to match worker.js path normalization
    if (config.url && config.url.length > 1 && config.url.endsWith('/')) {
      config.url = config.url.slice(0, -1);
    }
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for handling responses and errors
api.interceptors.response.use(
  (response) => {
    // Handle the response data
    return response;
  },
  (error) => {
    // Handle response errors
    console.error('API error:', error.message);
    
    // Check if we got a network error or a CORS error
    if (error.message === 'Network Error' || error.code === 'ERR_NETWORK') {
      console.warn('Network error detected - CORS issue might be occurring');
    }
    
    return Promise.reject(error);
  }
);

// Create a retry function for critical API calls
const withRetry = async (apiCall, maxRetries = 2, delay = 1000) => {
  let retries = 0;
  while (retries <= maxRetries) {
    try {
      return await apiCall();
    } catch (error) {
      if (retries === maxRetries) {
        throw error;
      }
      console.log(`Retry attempt ${retries + 1}/${maxRetries + 1}`);
      await new Promise(resolve => setTimeout(resolve, delay));
      retries++;
    }
  }
};

// Meal endpoints
export const getMeals = () => withRetry(() => api.get('/meals'));
export const getMeal = (id) => api.get(`/meals/${id}`);
export const createMeal = (data) => api.post('/meals', data);
export const updateMeal = (id, data) => api.put(`/meals/${id}`, data);
export const deleteMeal = (id) => api.delete(`/meals/${id}`);

// Spoonacular recipe search
export const searchRecipes = (query, mealType) => {
  return api.get('/meals/search_recipes', {
    params: {
      query,
      meal_type: mealType
    }
  });
};

export const addMealFromSpoonacular = (payload) => {
  return api.post('/meals/add_from_spoonacular', payload);
};

// Meal Plan endpoints
export const getMealPlans = () => withRetry(() => api.get('/mealplans'));
export const getMealPlan = (id) => api.get(`/mealplans/${id}`);
export const createMealPlan = (data) => api.post('/mealplans', data);
export const updateMealPlan = (id, data) => api.put(`/mealplans/${id}`);
export const deleteMealPlan = (id) => api.delete(`/mealplans/${id}`);
export const addToShoppingList = (mealPlanId) => 
  api.post(`/mealplans/${mealPlanId}/add_to_shopping_list`);

// Shopping List endpoints
export const getShoppingList = (completed = null) => {
  let params = {};
  if (completed !== null) {
    params.completed = completed;
  }
  return withRetry(() => api.get('/shopping-list', { params }));
};

export const getShoppingListItem = (id) => api.get(`/shopping-list/${id}`);
export const createShoppingListItem = (data) => api.post('/shopping-list', data);
export const updateShoppingListItem = (id, data) => api.put(`/shopping-list/${id}`);
export const deleteShoppingListItem = (id) => api.delete(`/shopping-list/${id}`);

// Ingredient endpoints
export const getIngredients = () => api.get('/ingredients');
export const getIngredient = (id) => api.get(`/ingredients/${id}`);

export const rateMeal = (mealId, rating) => {
  return api.post(`/meals/${mealId}/rate_meal`, { rating });
};

export const clearMealFromPlan = (planId, mealType) => {
  const updateData = {
    [mealType]: null
  };
  return api.patch(`/mealplans/${planId}`, updateData);
};

// Shared Shopping List endpoints
export const createSharedList = (title) => {
  return api.post('/shared-lists', { title });
};

export const getSharedList = (shareId) => {
  return api.get('/shared-lists/by_share_id', { params: { id: shareId } });
};

export const joinCollaborativeList = (shareId) => {
  return api.post('/shared-lists/join_collaborative', { share_id: shareId });
};

// Polling function to refresh data periodically for real-time collaboration
export const pollForChanges = async (shareId, currentItems, callback) => {
  try {
    const response = await getSharedList(shareId);
    const newItems = response.data.items;
    
    // Compare with current items to see if there are changes
    const hasChanges = JSON.stringify(newItems) !== JSON.stringify(currentItems);
    
    if (hasChanges && callback) {
      callback(newItems);
    }
    
    return hasChanges;
  } catch (error) {
    console.error("Error polling for changes:", error);
    return false;
  }
};

export default api; 